import { gql } from '@apollo/client'
import { Status as MockExamStatus } from '../../types'
import { Status as SimulatedStatus } from 'resources/simulateds/types'
import { diffInMinutes, withParams } from 'resources'
import { CUSTOM_SIMULATED_STATS, ENEM_SINGLE_SIMULATED } from 'routes'

/**
 * GraphQL query to fetch user mock exams.
 */
export const SIMULATED_QUERY = gql`
query UserMockExams {
    userMockExams {
        _id
        userId
        enemMockExams {
            _id
            createdAt
            endedAt
            maxEndTime
            migratedSimulatedId
            mockExamId
            score
            startedAt
            status
            type
            mockExam {
                _id
                code
                contextId
                durationTimeInMinutes
                title
                disciplines
                questions {
                  questionId
                }
                knowledgeAreasData {
                    id
                    name
                    slug
                }
            }
        }
        customMockExams {
            _id
            createdAt
            score
            startedAt
            status
            subjectsIds
            title
            description
            type
            questions {
              questionId
            }
            disciplineData {
                name
            }
            endedAt
            maxEndTime
        }
    }
}
`

/**
 * Maps a mock exam status to a simulated status.
 *
 * @param {string} mockExamStatus - The status of the mock exam.
 * @returns {string} The corresponding simulated status.
 */
export const mockExamStatusToSimulatedStatus = (mockExamStatus) => {
  switch (mockExamStatus) {
    case MockExamStatus.STARTED:
      return SimulatedStatus.STARTED
    case MockExamStatus.NOT_STARTED:
      return SimulatedStatus.NOT_STARTED
    case MockExamStatus.COMPLETED:
      return SimulatedStatus.FINISHED
    case MockExamStatus.PENDING:
      return SimulatedStatus.OPEN
    default:
      return SimulatedStatus.LOADING
  }
}

/**
 * Converts an array of enem mock exams to simulated objects.
 *
 * @param {Array<{ _id: string, mockExam: MockExam, createdAt: string, score: number, status: string, knowledgeAreasData: Array<Object>, questions: Array<Object> }>} mockExams - The array of enem mock exams.
 * @returns {Array<Simulated>} The array of simulated objects.
 */
export const enemMockExamsToSimulateds = (mockExams) =>
  mockExams
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .map(enemMockExamToSimulated)
export const customMockExamsToSimulateds = (mockExams) =>
  mockExams
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .map((mockExam) => ({
      id: mockExam._id,
      title: mockExam.title,
      description: mockExam.description || '',
      slug: mockExam._id,
      timeInMinutes: diffInMinutes(new Date(mockExam.endedAt || mockExam.maxEndTime), new Date(mockExam.startedAt)),
      numberOfQuestions: mockExam.questions ? mockExam.questions.length : 0,
      disciplines: mockExam.disciplineData.map(({ name }) => ({ name })),
      status: mockExamStatusToSimulatedStatus(mockExam.status),
      createdAt: mockExam.createdAt,
      myGrade: mockExam.score,
      isHidden: false,
      url: withParams(CUSTOM_SIMULATED_STATS, { userMockExamId: mockExam._id }),
    }))

/**
 * Converts a mock exam object to a simulated object.
 *
 * @param {{ _id: string, mockExam: MockExam, createdAt: string, score: number, status: string }} enemMockExam - The mock exam object to convert.
 * @returns {Simulated} The converted simulated object.
 */
const enemMockExamToSimulated = ({ _id, mockExam, createdAt, score, status }) => {
  const {
    title,
    code,
    durationTimeInMinutes,
    description,
    questions = [],
    knowledgeAreasData = [],
  } = mockExam || {
    title: '',
    code: '',
    durationTimeInMinutes: 0,
  }

  return {
    id: _id,
    title: title,
    slug: code,
    timeInMinutes: durationTimeInMinutes,
    description: description,
    numberOfQuestions: questions.length,
    disciplines: knowledgeAreasData.map(({ name }) => ({ name })),
    status: mockExamStatusToSimulatedStatus(status),
    createdAt: createdAt,
    myGrade: score,
    isHidden: false,
    url: withParams(ENEM_SINGLE_SIMULATED, { userMockExamId: _id }),
  }
}

/**
 * @typedef {Object} Simulated
 * @property {string} id - The unique identifier of the simulated.
 * @property {string} title - The title of the simulated.
 * @property {string} slug - The slug or code of the simulated.
 * @property {number} timeInMinutes - The duration of the simulated in minutes.
 * @property {string} description - A brief description of the simulated.
 * @property {number} numberOfQuestions - The total number of questions in the simulated.
 * @property {Array<Object>} disciplines - The knowledge areas or disciplines related to the simulated.
 * @property {string} status - The current status of the simulated (e.g., STARTED, NOT_STARTED, etc.).
 * @property {string} createdAt - The creation date of the simulated.
 * @property {number} myGrade - The user's grade or score for the simulated.
 * @property {boolean} isHidden - Whether the simulated is hidden or not.
 * @property {string} url - The URL to access the simulated.
 */

/**
 * @typedef {Object} MockExam
 * @property {string} _id - The unique identifier of the mock exam.
 * @property {string} code - The code of the mock exam.
 * @property {string} contextId - The context ID of the mock exam.
 * @property {string} description - A brief description of the mock exam.
 * @property {Array<Object>} disciplines - The disciplines related to the mock exam.
 * @property {number} durationTimeInMinutes - The duration of the mock exam in minutes.
 * @property {Array<Object>} knowledgeAreas - The knowledge areas covered by the mock exam.
 * @property {string} migratedSimulatedId - The ID of the migrated simulated exam.
 * @property {string} title - The title of the mock exam.
 * @property {Array<Object>} knowledgeAreasData - The knowledge areas data related to the mock exam.
 * @property {Array<Object>} questions - The questions included in the mock exam.
 */
